import React, { useEffect } from "react";

import { Container } from "@components/Container/Container";

import styles from "./styles.module.scss";
import aspectRatioStyles from "@components/AspectRatio/styles.module.scss";
import stylesHeading from "@components/Heading/styles.module.scss";
import { ImageField } from "@helpers/interfaces/image";
import { ArticleBox } from "@components/ArticleBox/ArticleBox";
import { Box } from "@components/Box/Box";
import classnames from "classnames";
import { LinkField } from "@helpers/interfaces/link";
import Anime from "react-anime";
import { animeChildrenProps } from "@helpers/animations";
import { useInView } from "react-intersection-observer";
import { Heading } from "@components/Heading/Heading";
import { VideoField } from "@helpers/interfaces/media";
import { Button } from "@components/Button/Button";
import Cross from "@svg-icons/cross.component.svg";
import playIcon from "@svg-icons/play.component.svg";
import arrowRight from "@svg-icons/arrow-right.component.svg";
import arrowLeft from "@svg-icons/arrow-left.component.svg";
import { Icon } from "@components/Icon/Icon";
import { AspectRatio } from "@components/AspectRatio/AspectRatio";
import { UmbracoVideo } from "@components/UmbracoVideo/UmbracoVideo";
import { UmbracoImage } from "@components/Image/UmbracoImage/UmbracoImage";
import { Text } from "@components/Text/Text";
import { createPortal } from "react-dom";


export interface spotProps {
  Key: string;
  Headline: string;
  Image: ImageField;
  Video?: {
    Video: VideoField;
  };
  Link: LinkField | string;
  ShowPlayIcon?: boolean;
  TeaserText: string;
  VideoFormat: string;
  RichText?: string;
}

export interface ArticleSpotsProps {
  headline?: string;
  spots: spotProps[];
  gridMode?: string;
  withLightbox?: boolean;
}

export interface ModalProps {
  section?: string;
  title: string;
  text?: string;
  spots: spotProps[];
  closeModal: () => void;
  spotIndex: number;
  showPlayIcon?: boolean;
  ratio?: string | string[];
  moveIndex: (direction: string) => void;
  
}

const Modal = ({closeModal, spots, section, title, text, spotIndex, ratio, showPlayIcon, moveIndex }: ModalProps) => {
  return (
  <Box 
    className={classnames(styles.modalBg)}>
      <Box
        className={classnames(styles.modalBox)}
      >
        <Box style={{
          width: "100%",
          display: "flex",
        }}>
          <Button autoFocus onClick={closeModal} style={{padding: "1rem", marginLeft: "auto"}} ><Icon svg={Cross} fill="black" stroke="black"/></Button>
        </Box>
        <Box style={{ overflow: "auto" }}>
            <Heading as="h2" className={classnames(styles.modalHeading)}>{section}</Heading>
            <Text className={classnames(styles.modalText)}>{title}</Text>
            <Box className={classnames(styles.modalContent)}>
            <>
              <AspectRatio
                ratio={spots[spotIndex].Video?.Video ? ratio ?? "16:9" : `${spots[spotIndex].Image.UmbracoWidth}:${spots[spotIndex].Image.UmbracoHeight}`} 
                className={classnames(aspectRatioStyles.aspectratioGallery)}
              >
                <>
                  { spots[spotIndex].Video?.Video && <UmbracoVideo video={spots[spotIndex].Video?.Video} />}
                  {spots[spotIndex].Image && !spots[spotIndex].Video?.Video && (
                    <>
                        <UmbracoImage
                          image={spots[spotIndex].Image}
                          style={{objectFit: "contain", maxWidth: "100%", maxHeight: "100%"}}
                          isCover
                          width={[350, 650, 650, 650]}
                        />
                      
                      {showPlayIcon && (
                        <Icon svg={playIcon} className={styles.playIcon} />
                      )}
                    </>
                  )}
                </>
              </AspectRatio>
              </>
            </Box>
            {text && 
              <Box className={classnames(styles.modalText)} dangerouslySetInnerHTML={{ __html: text }} />
            }
            </Box>
            <Box>
              <Text className={classnames(styles.modalPagination)}>
                {spotIndex + 1} / {spots.length}
              </Text>
              </Box>
              <Box className="buttons">
                <Button
                  className={classnames(styles.modalButton, styles.modalButtonPrev)} 
                  disabled={spotIndex === 0}
                  onClick={() => moveIndex("prev")}
                >
                  <Icon svg={arrowLeft} fill="black" stroke="black" strokeWidth="2"/>
                </Button>
                <Button 
                  className={classnames(styles.modalButton, styles.modalButtonNext)} 
                  disabled={spotIndex === spots.length - 1}
                  onClick={() => moveIndex("next")}>
                  <Icon svg={arrowRight} fill="black" stroke="black" strokeWidth="2"/>
                </Button>
              </Box>  
      </Box>

  </Box>
  )
}


const ArticleSpots: React.FC<ArticleSpotsProps> = ({
  headline,
  spots,
  gridMode,
  withLightbox = false,
  ...props
}) => {

  const [modalOpen, setModalOpen] = React.useState(false);
  const [spotIndex, setSpotIndex] = React.useState(0);

  const closeModal = () => {
    setModalOpen(false);
  }

  useEffect(() => {
    if (modalOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }
  }, [modalOpen]);
  
  const moveIndex = (direction: string) => {
    if (direction === "prev") {
      setSpotIndex(spotIndex - 1);
    }
    if (direction === "next") {
      setSpotIndex(spotIndex + 1);
    }
  }
  const { ref, inView } = useInView({
    threshold: 0.2
  });

  globalThis.console.log("inView ArticleSpots", inView);

  
  const focusMode = gridMode === "FocusMode";
  return (
    <>
    <div
      ref={ref}
      className={classnames(styles.articleSpots, styles.articleSpotsfocusGrid)}
      {...props}
    >
      <Container className="article-spots">
        {headline && (
          <Heading as="h2" className={stylesHeading.headingRibbon}>
            {headline}
          </Heading>
        )}
        <Box
          className={
            gridMode === "2ColumnMode"
              ? styles.gridTwo
              : gridMode === "3ColumnMode"
              ? styles.gridThree
              : styles.focusGrid
          }
        >
          {spots &&
            spots.map((spot: spotProps, index: number) => {              
              const third = index % 3 === 0;
              const animateEachChild = {
                ...animeChildrenProps,
                ...{ delay: (index * 100) / 1.5 },
                ...(inView ? { autoplay: true } : { autoplay: false })
              };

              // Image size for each size of article spots
              let imageWidthSizes = [110, 210, 300, 410]; // 3ColumnMode
              let imageHeightSizes = [62, 118, 169, 231]; // 3ColumnMode
              let sectionSize = "small";
              if (gridMode === "2ColumnMode") {
                imageWidthSizes = [350, 700, 700, 650]; // 2ColumnMode
                imageHeightSizes = [197, 394, 394, 366]; // 2ColumnMode
                sectionSize = "large";
              }
              if (focusMode && !third) {
                imageWidthSizes = [110, 210, 450, 420];
                imageHeightSizes = [62, 118, 253, 236];
                sectionSize = "small";
              }
              if (focusMode && third) {
                imageWidthSizes = [370, 730, 600, 830];
                imageHeightSizes = [208, 411, 337, 467];
                sectionSize = "xlarge";
              }

              // Spot size
              let spotFontSize = "medium";
              if (gridMode === "2ColumnMode" || (focusMode && third)) {
                spotFontSize = "large";
              }
              if (focusMode && !third) spotFontSize = "medium";

              const url =
                typeof spot.Link === "string" ? spot.Link : spot.Link?.Url;
              if (withLightbox) {
                return(
                <Button
                  key={spot.Key}
                  style={{textAlign: "start"}}
                  {...(focusMode && { className: styles.focusGridCell })}
                  onClick={() => {setSpotIndex(index); setModalOpen(true)} }
                >
                  <Anime {...animateEachChild}>
                    <ArticleBox
                      withLightbox={withLightbox}
                      openModal={() => {setSpotIndex(index); setModalOpen(true)} }
                      image={spot.Image}
                      {...(spot.Video && { video: spot.Video.Video })}
                      imageWidth={imageWidthSizes}
                      imageHeight={imageHeightSizes}
                      section={spot.Headline}
                      title={spot.TeaserText}
                      {...(spot.Link && {
                        url: url,
                        target:
                          typeof spot.Link !== "string" && spot.Link?.Target
                      })}
                      showPlayIcon={spot.ShowPlayIcon}
                      fontSize={spotFontSize}
                      sectionVariant={sectionSize}
                      ratio={
                       spot?.VideoFormat === "Brug 4:3 Format"
                         ? "4:3"
                        : "16:9"
                      }
                    />
                  </Anime>
                </Button>
                )
              } else {
              return (
                <Box
                  key={spot.Key}
                  {...(focusMode && { className: styles.focusGridCell })}
                >
                  <Anime {...animateEachChild}>
                    <ArticleBox
                      withLightbox={withLightbox}
                      openModal={() => {setSpotIndex(index); setModalOpen(true)} }
                      image={spot.Image}
                      {...(spot.Video && { video: spot.Video.Video })}
                      imageWidth={imageWidthSizes}
                      imageHeight={imageHeightSizes}
                      section={spot.Headline}
                      title={spot.TeaserText}
                      {...(spot.Link && {
                        url: url,
                        target:
                          typeof spot.Link !== "string" && spot.Link?.Target
                      })}
                      showPlayIcon={spot.ShowPlayIcon}
                      fontSize={spotFontSize}
                      sectionVariant={sectionSize}
                      ratio={
                       spot?.VideoFormat === "Brug 4:3 Format"
                         ? "4:3"
                        : "16:9"
                      }
                    />
                  </Anime>
                </Box>
              );
                    }
            })}
        </Box>
      </Container>
      </div>
      {withLightbox && modalOpen && 
      createPortal(
        <Modal 
          closeModal={closeModal} 
          spots={spots} 
          spotIndex={spotIndex}
          section={spots[spotIndex].Headline}
          title={spots[spotIndex].TeaserText}
          text={spots[spotIndex].RichText}  
          showPlayIcon={spots[spotIndex].ShowPlayIcon}
          moveIndex={moveIndex}
          ratio={
            spots[spotIndex]?.VideoFormat === "Brug 4:3 Format"
              ? "4:3"
             : spots[spotIndex]?.VideoFormat === "Brug 9:16 Format" 
             ? "9:16" 
             : "16:9"
           }
          />,
        document.body
        
      )
        }
    </>
  );
};

export { ArticleSpots };
